/** Cliente HTTP del PWA: sesion en cookies + CSRF (mismo contrato que la web). */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { clearReadCaches } from '@/utils/pwaCache';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';
const CSRF_COOKIE = 'erpa_csrf';

function csrfToken(): string | null {
  const entry = document.cookie.split('; ').find((part) => part.startsWith(`${CSRF_COOKIE}=`));
  return entry ? decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1)) : null;
}

function addCsrf(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const method = config.method?.toUpperCase() ?? 'GET';
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) config.headers.set('X-CSRF-Token', token);
  }
  return config;
}

export const mobileApiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

mobileApiClient.interceptors.request.use(addCsrf);

let refreshPromise: Promise<void> | null = null;

function isMobileProtectedPath(pathname: string): boolean {
  return pathname.startsWith('/m') && pathname !== '/m/login';
}

mobileApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const path = request?.url ?? '';
    const isAuthAction = /\/api\/v1\/auth\/(login|refresh|logout|forgot-password|reset-password)$/.test(
      path,
    );
    if (error.response?.status !== 401 || !request || request._retry || isAuthAction) {
      return Promise.reject(error);
    }

    request._retry = true;
    try {
      refreshPromise ??= mobileApiClient
        .post('/api/v1/auth/refresh')
        .then(() => undefined)
        .finally(() => {
          refreshPromise = null;
        });
      await refreshPromise;
      return mobileApiClient(request);
    } catch (refreshError) {
      // En v1 el PWA no expulsa por inactividad: la sesion vive mientras el
      // refresh de 7 dias rote. Solo se redirige al login en rutas protegidas.
      if (isMobileProtectedPath(window.location.pathname)) {
        await clearReadCaches();
        window.location.href = '/m/login';
      }
      return Promise.reject(refreshError);
    }
  },
);

export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail.replace(/^[A-Z][A-Z_]*:\s*/, '');
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Error desconocido';
}

export function extractRevisionConflict(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 409) return false;
  const code = error.response?.data?.code;
  if (typeof code === 'string') return code === 'REVISION_CONFLICT';
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail.startsWith('REVISION_CONFLICT');
}

/**
 * UUID v4 con respaldo para HTTP por IP/LAN.
 *
 * `crypto.randomUUID` solo existe en contexto seguro (HTTPS o localhost).
 * `crypto.getRandomValues` sigue disponible en HTTP; si tampoco existe, se
 * usa una combinacion de tiempo y azar para no bloquear la operacion.
 */
function newUuid(): string {
  const cripto = globalThis.crypto;
  if (typeof cripto?.randomUUID === 'function') return cripto.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof cripto?.getRandomValues === 'function') {
    cripto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
    const stamp = Date.now();
    for (let index = 0; index < 6; index += 1) {
      bytes[index] = (stamp >> (index * 8)) & 0xff;
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

/** Clave de idempotencia por intencion (una por intento del operador). */
export function newIdempotencyKey(): string {
  return newUuid();
}
